import { csiAPI } from "src/boot/axios"
import { CSIurl } from "src/util/enums/urls"
import {
  appendAttachmentToFormData,
  concatUrl,
  getCustomConfig,
  getNewAbortHandler,
  handleAttachmentBatchResponses,
} from "src/util/http/httpHelper"
import { Attachment, AttachmentUpload } from "src/util/types/dataModels/attachment"
import { BatchErrors, ObjectList, PayloadEntity } from "src/util/types/dataModels/genericObject"
import { HttpConfig } from "src/util/types/dataModels/http"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_READ,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_WORK_ORDERS,
} from "src/util/types/dataModels/user"
import {
  WorkOrderCorrectiveItem,
  WorkOrderCorrectiveItemFilterParams,
  WorkOrderRegularItem,
  WorkOrderRegularItemFilterParams,
} from "src/util/types/dataModels/workOrder"
import {
  WorkOrderCommentFilterParams,
  WorkOrderCorrectiveItemComment,
  WorkOrderRegularItemComment,
} from "src/util/types/dataModels/workOrderComment"

//region Work Order Corrective

//region Work Order Corrective Item

export async function getWorkOrderCorrectiveItemList(
  config: HttpConfig<WorkOrderCorrectiveItemFilterParams> = {}
): Promise<ObjectList<WorkOrderCorrectiveItem>> {
  const entityURL = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function postWorkOrderCorrectiveItem(
  config: HttpConfig = {},
  payload: PayloadEntity<WorkOrderCorrectiveItem>
): Promise<WorkOrderCorrectiveItem> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, payload, { signal, customConfig })

  return data
}

export async function getWorkOrderCorrectiveItem(
  config: HttpConfig = {},
  workOrderId: string
): Promise<WorkOrderCorrectiveItem> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_ID.replace("{id}", workOrderId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function putWorkOrderCorrectiveItem(
  config: HttpConfig = {},
  workOrderId: string,
  payload: PayloadEntity<WorkOrderCorrectiveItem>
): Promise<WorkOrderCorrectiveItem> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_ID.replace("{id}", workOrderId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, payload, { signal, customConfig })

  return data
}

export async function patchWorkOrderCorrectiveItem(
  config: HttpConfig = {},
  workOrderId: string,
  payload: Partial<PayloadEntity<WorkOrderCorrectiveItem>>
): Promise<WorkOrderCorrectiveItem> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_ID.replace("{id}", workOrderId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, payload, { signal, customConfig })

  return data
}

//endregion

//region Work Order Corrective Item Attachment

export async function getWorkOrderCorrectiveItemAttachmentList(
  config: HttpConfig = {},
  workOrderId: string
): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_ATTACHMENTS.replace("{wo_id}", workOrderId)
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: ObjectList<Attachment> } = await csiAPI.get(url, { signal, customConfig })

  data.results = data.results.map((i) => {
    i.context = ["work_order_corrective_item"]
    return i
  })

  return data
}

export async function postWorkOrderCorrectiveItemAttachment(
  config: HttpConfig = {},
  workOrderId: string,
  attachment: AttachmentUpload
): Promise<Attachment> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_ATTACHMENTS.replace("{wo_id}", workOrderId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = appendAttachmentToFormData(attachment)

  const { data }: { data: Attachment } = await csiAPI.post(url, formData, {
    headers,
    signal,
    customConfig,
  })

  data.context = ["work_order_corrective_item"]

  return data
}

export async function getWorkOrderCorrectiveItemAttachment(
  config: HttpConfig = {},
  workOrderId: string,
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_ATTACHMENTS_ID.replace(
    "{wo_id}",
    workOrderId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: Attachment } = await csiAPI.get(url, { signal, customConfig })

  data.context = ["work_order_corrective_item"]

  return data
}

export async function deleteWorkOrderCorrectiveItemAttachment(
  config: HttpConfig = {},
  workOrderId: string,
  attachmentId: string
) {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_ATTACHMENTS_ID.replace(
    "{wo_id}",
    workOrderId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  return await csiAPI.delete(url, { signal, customConfig })
}

export async function getWorkOrderCorrectiveItemAttachmentContent(
  config: HttpConfig = {},
  workOrderId: string,
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_ATTACHMENTS_ID_CONTENT.replace(
    "{wo_id}",
    workOrderId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

export async function postWorkOrderCorrectiveItemAttachmentBatch(
  config: HttpConfig = {},
  workOrderId: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_ATTACHMENTS_BATCH.replace("{wo_id}", workOrderId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = { "Content-Type": "multipart/form-data" }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = new FormData()

  for (const attachment of attachments) {
    appendAttachmentToFormData(attachment, formData)
  }

  const response = await csiAPI.post(url, formData, { headers, signal, customConfig })

  return handleAttachmentBatchResponses(response, "work_order_corrective_item")
}

//endregion

//region Work Order Corrective Item Batch

export async function postWorkOrderCorrectiveItemBatch(
  config: HttpConfig = {},
  workOrders: PayloadEntity<WorkOrderCorrectiveItem>[]
): Promise<
  ObjectList<WorkOrderCorrectiveItem> | ObjectList<BatchErrors | WorkOrderCorrectiveItem>
> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, workOrders, { signal, customConfig })

  return data
}

export async function patchWorkOrderCorrectiveItemBatch(
  config: HttpConfig = {},
  workOrders: Partial<PayloadEntity<WorkOrderCorrectiveItem>>[]
): Promise<
  ObjectList<WorkOrderCorrectiveItem> | ObjectList<BatchErrors | WorkOrderCorrectiveItem>
> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, workOrders, { signal, customConfig })

  return data
}

//endregion

//region Work Order Corrective Item Comment

export async function getWorkOrderCorrectiveItemCommentList(
  config: HttpConfig<WorkOrderCommentFilterParams> = {}
): Promise<ObjectList<WorkOrderCorrectiveItemComment>> {
  const entityURL = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function postWorkOrderCorrectiveItemComment(
  config: HttpConfig = {},
  workOrderComment: PayloadEntity<WorkOrderCorrectiveItemComment>
): Promise<WorkOrderCorrectiveItemComment> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, workOrderComment, { signal, customConfig })

  return data
}

export async function getWorkOrderCorrectiveItemComment(
  config: HttpConfig = {},
  commentId: string
): Promise<WorkOrderCorrectiveItemComment> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function putWorkOrderCorrectiveItemComment(
  config: HttpConfig<WorkOrderCommentFilterParams> = {},
  commentId: string,
  comment: PayloadEntity<WorkOrderCorrectiveItemComment>
): Promise<WorkOrderCorrectiveItemComment> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, comment, { signal, customConfig })

  return data
}

export async function patchWorkOrderCorrectiveItemComment(
  config: HttpConfig = {},
  commentId: string,
  comment: Partial<PayloadEntity<WorkOrderCorrectiveItemComment>>
): Promise<WorkOrderCorrectiveItemComment> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, comment, { signal, customConfig })

  return data
}

//endregion

//region Work Order Corrective Item Comment Attachment

export async function getWorkOrderCorrectiveItemCommentAttachmentList(
  config: HttpConfig = {},
  commentId: string
): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS_ATTACHMENTS.replace(
    "{comment_id}",
    commentId
  )
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: ObjectList<Attachment> } = await csiAPI.get(url, { signal, customConfig })

  data.results = data.results.map((i) => {
    i.context = ["work_order_corrective_item_comment"]
    return i
  })

  return data
}

export async function postWorkOrderCorrectiveItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachment: AttachmentUpload
): Promise<Attachment> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS_ATTACHMENTS.replace(
    "{comment_id}",
    commentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = appendAttachmentToFormData(attachment)

  const { data }: { data: Attachment } = await csiAPI.post(url, formData, {
    headers,
    signal,
    customConfig,
  })

  data.context = ["work_order_corrective_item_comment"]

  return data
}

export async function getWorkOrderCorrectiveItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS_ATTACHMENTS_ID.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: Attachment } = await csiAPI.get(url, { signal, customConfig })

  data.context = ["work_order_corrective_item_comment"]

  return data
}

export async function deleteWorkOrderCorrectiveItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
): Promise<void> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS_ATTACHMENTS_ID.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

export async function getWorkOrderCorrectiveItemCommentAttachmentContent(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS_ATTACHMENTS_ID_CONTENT.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

export async function postWorkOrderCorrectiveItemCommentAttachmentBatch(
  config: HttpConfig = {},
  commentId: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS_ATTACHMENTS_BATCH.replace(
    "{comment_id}",
    commentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = { "Content-Type": "multipart/form-data" }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = new FormData()

  for (const attachment of attachments) {
    appendAttachmentToFormData(attachment, formData)
  }

  const response = await csiAPI.post(url, formData, { headers, signal, customConfig })

  return handleAttachmentBatchResponses(response, "work_order_corrective_item_comment")
}

//endregion

//region Work Order Corrective Item Comment Batch

export async function postWorkOrderCorrectiveItemCommentBatch(
  config: HttpConfig = {},
  comments: PayloadEntity<WorkOrderCorrectiveItemComment>[]
): Promise<
  | ObjectList<WorkOrderCorrectiveItemComment>
  | ObjectList<BatchErrors | WorkOrderCorrectiveItemComment>
> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, comments, { signal, customConfig })

  return data
}

export async function patchWorkOrderCorrectiveItemCommentBatch(
  config: HttpConfig = {},
  comments: Partial<PayloadEntity<WorkOrderCorrectiveItemComment>>[]
): Promise<
  | ObjectList<WorkOrderCorrectiveItemComment>
  | ObjectList<BatchErrors | WorkOrderCorrectiveItemComment>
> {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, comments, { signal, customConfig })

  return data
}

//endregion

//endregion

//region Work Order Regular

//region Work Order Regular Item

export async function getWorkOrderRegularItemList(
  config: HttpConfig<WorkOrderRegularItemFilterParams> = {}
): Promise<ObjectList<WorkOrderRegularItem>> {
  const entityURL = CSIurl.WORK_ORDER_REGULAR_ON_ITEM
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function postWorkOrderRegularItem(
  config: HttpConfig = {},
  payload: PayloadEntity<WorkOrderRegularItem>
): Promise<WorkOrderRegularItem> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, payload, { signal, customConfig })

  return data
}

export async function getWorkOrderRegularItem(
  config: HttpConfig = {},
  workOrderId: string
): Promise<WorkOrderRegularItem> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_ID.replace("{id}", workOrderId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function putWorkOrderRegularItem(
  config: HttpConfig = {},
  workOrderId: string,
  payload: PayloadEntity<WorkOrderRegularItem>
): Promise<WorkOrderRegularItem> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_ID.replace("{id}", workOrderId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, payload, { signal, customConfig })

  return data
}

export async function patchWorkOrderRegularItem(
  config: HttpConfig = {},
  workOrderId: string,
  payload: Partial<PayloadEntity<WorkOrderRegularItem>>
): Promise<WorkOrderRegularItem> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_ID.replace("{id}", workOrderId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, payload, { signal, customConfig })

  return data
}

//endregion

//region Work Order Corrective Item Attachment

export async function getWorkOrderRegularItemAttachmentList(
  config: HttpConfig = {},
  workOrderId: string
): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_ATTACHMENTS.replace("{wo_id}", workOrderId)
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: ObjectList<Attachment> } = await csiAPI.get(url, { signal, customConfig })

  data.results = data.results.map((i) => {
    i.context = ["work_order_regular_item"]
    return i
  })

  return data
}

export async function postWorkOrderRegularItemAttachment(
  config: HttpConfig = {},
  workOrderId: string,
  attachment: AttachmentUpload
): Promise<Attachment> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_ATTACHMENTS.replace("{wo_id}", workOrderId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = appendAttachmentToFormData(attachment)

  const { data }: { data: Attachment } = await csiAPI.post(url, formData, {
    headers,
    signal,
    customConfig,
  })

  data.context = ["work_order_regular_item"]

  return data
}

export async function getWorkOrderRegularItemAttachment(
  config: HttpConfig = {},
  workOrderId: string,
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_ATTACHMENTS_ID.replace(
    "{wo_id}",
    workOrderId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: Attachment } = await csiAPI.get(url, { signal, customConfig })

  data.context = ["work_order_regular_item"]

  return data
}

export async function deleteWorkOrderRegularItemAttachment(
  config: HttpConfig = {},
  workOrderId: string,
  attachmentId: string
) {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_ATTACHMENTS_ID.replace(
    "{wo_id}",
    workOrderId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  return await csiAPI.delete(url, { signal, customConfig })
}

export async function getWorkOrderRegularItemAttachmentContent(
  config: HttpConfig = {},
  workOrderId: string,
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_ATTACHMENTS_ID_CONTENT.replace(
    "{wo_id}",
    workOrderId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

export async function postWorkOrderRegularItemAttachmentBatch(
  config: HttpConfig = {},
  workOrderId: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_ATTACHMENTS_BATCH.replace("{wo_id}", workOrderId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = { "Content-Type": "multipart/form-data" }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = new FormData()

  for (const attachment of attachments) {
    appendAttachmentToFormData(attachment, formData)
  }

  const response = await csiAPI.post(url, formData, { headers, signal, customConfig })

  return handleAttachmentBatchResponses(response, "work_order_regular_item")
}

//endregion

//region Work Order Corrective Item Batch

export async function postWorkOrderRegularItemBatch(
  config: HttpConfig = {},
  workOrders: PayloadEntity<WorkOrderRegularItem>[]
): Promise<ObjectList<WorkOrderRegularItem>> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, workOrders, { signal, customConfig })

  return data
}

export async function patchWorkOrderRegularItemBatch(
  config: HttpConfig = {},
  workOrders: Partial<PayloadEntity<WorkOrderRegularItem>>[]
): Promise<ObjectList<WorkOrderRegularItem> | ObjectList<BatchErrors | WorkOrderRegularItem>> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, workOrders, { signal, customConfig })

  return data
}

//endregion

//region Work Order Corrective Item Comment

export async function getWorkOrderRegularItemCommentList(
  config: HttpConfig<WorkOrderCommentFilterParams> = {}
): Promise<ObjectList<WorkOrderRegularItemComment>> {
  const entityURL = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function postWorkOrderRegularItemComment(
  config: HttpConfig = {},
  workOrderComment: PayloadEntity<WorkOrderRegularItemComment>
): Promise<WorkOrderRegularItemComment> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, workOrderComment, { signal, customConfig })

  return data
}

export async function getWorkOrderRegularItemComment(
  config: HttpConfig = {},
  commentId: string
): Promise<WorkOrderRegularItemComment> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function putWorkOrderRegularItemComment(
  config: HttpConfig<WorkOrderCommentFilterParams> = {},
  commentId: string,
  comment: PayloadEntity<WorkOrderRegularItemComment>
): Promise<WorkOrderRegularItemComment> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, comment, { signal, customConfig })

  return data
}

export async function patchWorkOrderRegularItemComment(
  config: HttpConfig = {},
  commentId: string,
  comment: Partial<PayloadEntity<WorkOrderRegularItemComment>>
): Promise<WorkOrderRegularItemComment> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, comment, { signal, customConfig })

  return data
}

//endregion

//region Work Order Corrective Item Comment Attachment

export async function getWorkOrderRegularItemCommentAttachmentList(
  config: HttpConfig = {},
  commentId: string
): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS_ATTACHMENTS.replace(
    "{comment_id}",
    commentId
  )
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: ObjectList<Attachment> } = await csiAPI.get(url, { signal, customConfig })

  data.results = data.results.map((i) => {
    i.context = ["work_order_regular_item_comment"]
    return i
  })

  return data
}

export async function postWorkOrderRegularItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachment: AttachmentUpload
): Promise<Attachment> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS_ATTACHMENTS.replace(
    "{comment_id}",
    commentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = appendAttachmentToFormData(attachment)

  const { data }: { data: Attachment } = await csiAPI.post(url, formData, {
    headers,
    signal,
    customConfig,
  })

  data.context = ["work_order_regular_item_comment"]

  return data
}

export async function getWorkOrderRegularItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS_ATTACHMENTS_ID.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: Attachment } = await csiAPI.get(url, { signal, customConfig })

  data.context = ["work_order_regular_item_comment"]

  return data
}

export async function deleteWorkOrderRegularItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
): Promise<void> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS_ATTACHMENTS_ID.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

export async function getWorkOrderRegularItemCommentAttachmentContent(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS_ATTACHMENTS_ID_CONTENT.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_WORK_ORDERS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

export async function postWorkOrderRegularItemCommentAttachmentBatch(
  config: HttpConfig = {},
  commentId: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS_ATTACHMENTS_BATCH.replace(
    "{comment_id}",
    commentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = { "Content-Type": "multipart/form-data" }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = new FormData()

  for (const attachment of attachments) {
    appendAttachmentToFormData(attachment, formData)
  }

  const response = await csiAPI.post(url, formData, { headers, signal, customConfig })

  return handleAttachmentBatchResponses(response, "work_order_regular_item_comment")
}

//endregion

//region Work Order Corrective Item Comment Batch

export async function postWorkOrderRegularItemCommentBatch(
  config: HttpConfig = {},
  comments: PayloadEntity<WorkOrderRegularItemComment>[]
): Promise<
  ObjectList<WorkOrderRegularItemComment> | ObjectList<BatchErrors | WorkOrderRegularItemComment>
> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, comments, { signal, customConfig })

  return data
}

export async function patchWorkOrderRegularItemCommentBatch(
  config: HttpConfig = {},
  comments: Partial<PayloadEntity<WorkOrderRegularItemComment>>[]
): Promise<
  ObjectList<WorkOrderRegularItemComment> | ObjectList<BatchErrors | WorkOrderRegularItemComment>
> {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_ORDERS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, comments, { signal, customConfig })

  return data
}

//endregion

//endregion
